import {
  Center,
  Group,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Text,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react"
import { Button, ActionIcon, SectionHeader, Loader } from "@tfg/ui"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { ContactResponses } from "@tfg/types/endpoints/contacts"

import { DeleteContactModal } from "~/pages/SupplierPortal/Contacts/DeleteContactModal.tsx"
import useContactForm, {
  contactToSupplierMapping,
} from "~/pages/SupplierPortal/Contacts/hooks/useContactForm"
import { useListSupplierContactsQuery } from "~/pages/SupplierPortal/Contacts/hooks/useListSupplierContactQuery.ts"
import { ContactDrawer } from "~/pages/SupplierPortal/TraceabilityRequests/components/ContactDrawer"
import { useAuth } from "~/store/auth-context.tsx"

export const SupplierContacts = () => {
  const { t } = useTranslation(["supplier", "organizationSettings"])
  const { isAdmin } = useAuth()

  const {
    data: contacts,
    isPending: isLoading,
    isError,
  } = useListSupplierContactsQuery()
  const { contact, setContact, reset, handleContact } = useContactForm({})
  const [isModalOpened, modalActions] = useDisclosure(false)
  const [valuesUpdated, setValuesUpdated] = useState(false)
  const [openedDelete, { open: openDelete, close: closeDelete }] =
    useDisclosure()
  const [deletedContact, setDeletedContact] =
    useState<ContactResponses.Contact>()

  useEffect(() => {
    if (valuesUpdated) {
      modalActions.open()
      setValuesUpdated(false)
    }
  }, [valuesUpdated, modalActions, setValuesUpdated])

  const isUpdate = "supplierId" in contact && contact.supplierId !== ""

  if (isLoading) {
    return <Loader fullPage />
  } else if (isError) {
    return (
      <Center h="100%">
        <Text c="red" fz="xl">
          {t("request.errors.generic")}
        </Text>
      </Center>
    )
  }
  return (
    <>
      <SectionHeader
        mainCta={
          <Button
            icon={<IconPlus />}
            onClick={() => {
              reset()
              setValuesUpdated(true)
            }}
          >
            {t("contacts.buttons.add")}
          </Button>
        }
      />
      <Table verticalSpacing="sm" highlightOnHover>
        <TableThead>
          <TableTr>
            {isAdmin ? (
              <TableTh>
                <Text c="violet">{t("contacts.table.headers.id")}</Text>
              </TableTh>
            ) : null}
            <TableTh>{t("contacts.table.headers.name")}</TableTh>
            <TableTh>{t("contacts.table.headers.email")}</TableTh>
            <TableTh>{t("contacts.table.headers.actions")}</TableTh>
          </TableTr>
        </TableThead>
        <TableTbody>
          {contacts?.map((contact) => (
            <TableTr key={contact.id}>
              {isAdmin ? (
                <TableTd>
                  <Text c="violet">{contact.id}</Text>
                </TableTd>
              ) : null}
              <TableTd>{contact.user.name}</TableTd>
              <TableTd>{contact.user.email}</TableTd>
              <TableTd style={{ minWidth: "240px" }}>
                <Group wrap="nowrap">
                  <ActionIcon
                    icon={<IconEdit />}
                    onClick={() => {
                      setContact(contactToSupplierMapping(contact))
                      setValuesUpdated(true)
                    }}
                  />
                  <ActionIcon
                    icon={<IconTrash />}
                    onClick={() => {
                      setDeletedContact(contact)
                      openDelete()
                    }}
                  />
                </Group>
              </TableTd>
            </TableTr>
          ))}
        </TableTbody>
      </Table>
      <ContactDrawer
        opened={isModalOpened}
        setModalOpened={modalActions}
        supplierMapping={contact}
        handleSupplierMapping={handleContact}
        isUpdate={isUpdate}
      />
      {deletedContact && (
        <DeleteContactModal
          isOpen={openedDelete}
          onClose={() => closeDelete()}
          contact={deletedContact}
        />
      )}
    </>
  )
}
